import { ReactNode, useCallback } from "react";
import styles from "./table.module.css";
import { Button, LoadingSkeleton, Row, Pagination } from "@narmi/design_system";
import { useEffect, useContext, useState } from "react";

import NotificationContext from "cerulean/src/NotificationContext";
import FilterDialog from "./FilterDialog";
import { PaymentQuery } from "./FilterDialog";
import PaymentDetailDrawer from "./PaymentDetailDrawer";
import SearchBar from "./SearchBar";
import { PAYMENT_DIRECTIONS_TYPE, PAYMENT_STATUSES, PAYMENT_STATUSES_TYPE } from "./codesets";

export const moneyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

export type Payment = {
  payment_identification: string;
  source_fi: string;
  source_fi_name: string;
  destination_fi: string;
  destination_fi_name: string;
  source_account: string;
  destination_account: string;
  source_name: string;
  destination_name: string;
  amount_cents: number;
  created_at: string;
  memo: string;
  updated_at: string;
  state: PAYMENT_STATUSES_TYPE;
  type: PAYMENT_DIRECTIONS_TYPE;
  fed_settlement_date: string;
  core_transaction_id: string;
};

export default function PaymentTable({ selectedId }: { selectedId: string | undefined }) {
  const { sendNotification } = useContext(NotificationContext);

  const [payments, setPayments] = useState([]);
  const [page, setPage] = useState(1);
  const [availablePages, setAvailablePages] = useState([1]);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedPayment, setSelectedPayment]: [Payment | null, CallableFunction] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [filterTags, setFilterTags]: [Array<ReactNode>, CallableFunction] = useState([]);

  const [filterQuery, setFilterQuery]: [PaymentQuery, CallableFunction] = useState({
    start_date: undefined,
    state: undefined,
    end_date: undefined,
    from_amount: undefined,
    to_amount: undefined,
    direction: undefined,
    text: undefined,
  });

  useEffect(() => {
    window.scrollTo({ top: 200, left: 0, behavior: "smooth" });
  }, []);

  async function fetchPayments(page: number = 1, query: string | null = null) {
    setIsLoading(true);
    const query_str = query === null || query === "" ? "" : `&query=${query}`;
    const res = await fetch(`/v1/fednow/payments?ordering=updated_at&page=${page}${query_str}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });
    if (!res.ok) {
      let errorMessage = "Error fetching payments. Please contact support.";
      try {
        const errData = await res.json();
        if (typeof errData.message === "string") {
          errorMessage = errData.message;
        } else {
          errorMessage = String(Object.values(errData)[0]) || errorMessage;
        }
      } finally {
        sendNotification({
          type: "negative",
          text: errorMessage,
        });
        setIsLoading(false);
      }
      return;
    }
    const data = await res.json();
    const results = data.results.sort((a: Payment, b: Payment) => {
      return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
    });
    setPayments(results);
    if (data.next && availablePages.indexOf(page + 1) < 0) {
      setAvailablePages(availablePages.concat([page + 1]));
    } else if (!data.next && availablePages.indexOf(page + 1) >= 0) {
      const pgCopy = availablePages.slice();
      pgCopy.splice(availablePages.indexOf(page + 1), 1);
      setAvailablePages(pgCopy);
    }
    // An ID was passed into the URL
    setIsLoading(false);
    if (selectedId && selectedId.length > 0) {
      const lookupRes = await fetch(`/v1/fednow/payments/${encodeURIComponent(selectedId)}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });
      if (!lookupRes.ok) {
        return;
      }
      const pmt = await lookupRes.json();
      setSelectedPayment(pmt);
      setDrawerOpen(true);
    }
  }

  async function refreshPayments(page: number = 1, query: PaymentQuery | null = null) {
    const copy = { ...query };
    if (copy?.from_amount) {
      copy.from_amount = String(Number(copy.from_amount) * 100);
    }
    if (copy?.to_amount) {
      copy.to_amount = String(Number(copy.to_amount) * 100);
    }
    const queryStr = query ? JSON.stringify(copy) : null;
    await fetchPayments(page, queryStr);
  }

  useEffect(() => {
    refreshPayments();
  }, []);

  return (
    <>
      <SearchBar
        submitCallback={refreshPayments}
        setPage={setPage}
        filterQuery={filterQuery}
        setFilterQuery={setFilterQuery}
        setFilterDialogOpen={setFilterDialogOpen}
      />
      <FilterDialog
        type="payments"
        filterQuery={filterQuery}
        setFilterQuery={setFilterQuery}
        dialogOpen={filterDialogOpen}
        setDialogOpen={setFilterDialogOpen}
        applyCallback={useCallback(() => {
          setPage(1);
          refreshPayments(1, filterQuery);
        }, [filterQuery, page])}
        filterTags={filterTags}
        setFilterTags={(value: Array<ReactNode>) => {
          setFilterTags(value);
          setPage(1);
          refreshPayments(1, filterQuery);
        }}
      />
      {payments.length > 0 || isLoading ? (
        <div>
          <PaymentTableHeader />
          <div className={styles.fednowTable}>
            <LoadingSkeleton isLoading={isLoading} showTitle={false} lines={5}>
              {payments.map((p: Payment) => (
                <PaymentRow
                  payment={p}
                  key={p.payment_identification}
                  clickCallback={() => {
                    setSelectedPayment(p);
                    setDrawerOpen(true);
                  }}
                />
              ))}
            </LoadingSkeleton>
          </div>
        </div>
      ) : (
        <h4 className="fontFamily--body padding--y--l">
          No payments found.{" "}
          <Button
            kind="plain"
            label="Clear search"
            onClick={() => {
              setFilterQuery({});
              setFilterTags([]);
              refreshPayments();
            }}
          />
        </h4>
      )}
      <div className="margin--top">
        <Pagination
          selectedPage={page}
          onPageChange={(p: number) => {
            setPage(p);
            refreshPayments(p, filterQuery);
          }}
          totalPages={availablePages.length}
        />
      </div>
      <PaymentDetailDrawer
        isOpen={drawerOpen}
        setIsOpen={setDrawerOpen}
        payment={selectedPayment}
        refresh={refreshPayments}
      />
    </>
  );
}

const PaymentTableHeader = () => (
  <div className="padding--bottom--xs padding--top--s padding--x" style={{ width: "100%" }}>
    <Row alignItems="center">
      <Row.Item>
        <div className={`fontWeight--bold ${styles.columnHeaderText}`}>PAYMENT ID</div>
      </Row.Item>
      <Row.Item>
        <div className={`fontWeight--bold ${styles.columnHeaderText}`}>SOURCE</div>
      </Row.Item>
      <Row.Item>
        <div className={`fontWeight--bold ${styles.columnHeaderText}`}>DESTINATION</div>
      </Row.Item>
      <Row.Item>
        <div className={`fontWeight--bold ${styles.columnHeaderText}`}>LAST UPDATED</div>
      </Row.Item>
      <Row.Item>
        <div className={`fontWeight--bold ${styles.columnHeaderText}`}>STATUS</div>
      </Row.Item>
      <Row.Item shrink>
        <div className={`fontWeight--bold ${styles.columnHeaderText} ${styles.lastRowColumn}`}>
          AMOUNT
        </div>
      </Row.Item>
    </Row>
  </div>
);

const PaymentRow = ({
  payment,
  clickCallback,
}: {
  payment: Payment;
  clickCallback: CallableFunction;
}) => {
  return (
    <div
      className={`${styles.fednowTableRow} padding--y--s padding--x`}
      style={{ width: "100%" }}
      onClick={() => {
        clickCallback();
      }}
    >
      <Row alignItems="center">
        <Row.Item>
          <p className="whiteSpace--truncate">{payment.payment_identification}</p>
        </Row.Item>
        <Row.Item>
          <div>{payment.source_account}</div>
          <small>{payment.source_fi_name}</small>
        </Row.Item>
        <Row.Item>
          <div>{payment.destination_account}</div>
          <small>{payment.destination_fi_name}</small>
        </Row.Item>
        <Row.Item>
          <div>{new Date(payment.updated_at).toLocaleString()}</div>
        </Row.Item>
        <Row.Item>
          <div>{PAYMENT_STATUSES[payment.state]}</div>
        </Row.Item>
        <Row.Item shrink>
          <div className={styles.lastRowColumn}>
            {moneyFormatter.format(payment.amount_cents / 100)}
          </div>
        </Row.Item>
      </Row>
    </div>
  );
};
